// src/landing/blog-catalog.js
// ─── Hub /ressources : catalogue éditorial (§76-109) ────────────────────────
// Vingt sujets, sept catégories, un auteur identifié. Chaque entrée porte son
// mot-clé, son slug propre, sa catégorie, ses sources à citer et le plan de
// ses H2 — ce qui permet de rédiger chaque article à un standard constant.
//
// RÈGLE §109 : rien n'est publié sans relecture, fact-check et sources. Le
// champ `status` distingue ce qui est rédigé et relu de ce qui n'est qu'un
// brief. Seuls les articles `published` apparaissent sur le site ; les briefs
// restent des briefs. Un catalogue de vingt titres n'est pas vingt articles.

export const AUTHOR = {
  name: "Rédaction VehicleIQ",
  role: "Équipe produit & marché VO",
  url: "/ressources",
};

export const CATEGORIES = [
  { id: "ia", label: "Intelligence artificielle" },
  { id: "achat", label: "Achat VO" },
  { id: "marge", label: "Estimation & marge" },
  { id: "historique", label: "Historique véhicule" },
  { id: "marche", label: "Marché automobile" },
  { id: "crm", label: "Gestion & CRM" },
  { id: "reglementation", label: "Réglementation" },
];

// Sources officielles privilégiées (§105)
const SRC = {
  histovec: { label: "HistoVec — Ministère de l'Intérieur", url: "https://histovec.interieur.gouv.fr" },
  servicePublic: { label: "service-public.fr", url: "https://www.service-public.fr" },
  ants: { label: "ANTS", url: "https://ants.gouv.fr" },
  impots: { label: "impots.gouv.fr", url: "https://www.impots.gouv.fr" },
  economie: { label: "economie.gouv.fr", url: "https://www.economie.gouv.fr" },
  dataGouv: { label: "data.gouv.fr", url: "https://www.data.gouv.fr" },
  ademe: { label: "ADEME", url: "https://www.ademe.fr" },
  insee: { label: "INSEE", url: "https://www.insee.fr" },
  cgi297a: { label: "CGI art. 297 A (TVA sur marge)", url: "https://www.legifrance.gouv.fr" },
};

export const ARTICLES = [
  { slug: "logiciel-vehicule-occasion-professionnel", category: "ia", status: "published",
    keyword: "logiciel véhicule occasion professionnel",
    title: "Quel logiciel choisir pour gérer des véhicules d'occasion en 2026 ?",
    excerpt: "Gestion de stock, estimation, historique, CRM, IA : ce qu'un logiciel VO doit vraiment faire, et comment comparer les offres sans se tromper.",
    published: "2026-09-04", updated: "2026-09-04", readingMin: 9,
    h2: ["Ce qu'un professionnel VO attend d'un logiciel en 2026", "Les six fonctions qui comptent vraiment", "Logiciel classique ou assistant IA : deux logiques",
         "Tableau comparatif des approches", "Comment évaluer un logiciel sur vos propres véhicules", "Coût réel : abonnement, temps gagné, erreurs évitées"],
    faq: ["Un logiciel VO remplace-t-il un expert ?", "Faut-il un logiciel différent pour un marchand et un garage ?", "Combien de temps pour être opérationnel ?"],
    sources: [SRC.histovec, SRC.servicePublic] },

  { slug: "logiciel-vo-fonctionnalites-utiles", category: "ia", status: "brief",
    keyword: "logiciel VO",
    title: "Logiciel VO : quelles fonctionnalités sont vraiment utiles aux marchands automobiles ?",
    excerpt: "Toutes les fonctionnalités ne se valent pas. Celles qui font gagner une heure par dossier, et celles qui décorent.",
    h2: ["Identification par plaque et VIN", "Historique et contrôle", "Analyse marché", "Marge et négociation", "Dossier véhicule et suivi", "Ce qui ne sert à rien"],
    faq: ["Faut-il un module de cote ?", "Un CRM est-il indispensable ?"], sources: [SRC.histovec] },

  { slug: "intelligence-artificielle-achat-revente-automobile", category: "ia", status: "brief",
    keyword: "intelligence artificielle automobile",
    title: "Comment l'intelligence artificielle transforme l'achat-revente automobile",
    excerpt: "De l'analyse photo au calcul de marge : ce que l'IA change concrètement dans le quotidien d'un négociant.",
    h2: ["Ce que l'IA sait faire aujourd'hui", "Vision : lire l'état d'un véhicule", "Documents : HistoVec, factures, cartes grises", "Marché : comparer sans y passer la journée", "Ce que l'IA ne fait pas", "Par où commencer"],
    faq: ["L'IA peut-elle estimer un véhicule ?", "Mes données sont-elles utilisées pour entraîner des modèles ?"], sources: [] },

  { slug: "ia-vehicule-occasion-usages-professionnel", category: "ia", status: "brief",
    keyword: "IA véhicule occasion",
    title: "IA et véhicule d'occasion : 10 usages concrets pour un professionnel VO",
    excerpt: "Dix situations réelles, de la reprise au dépôt d'annonce, où une IA métier fait la différence.",
    h2: ["1 à 10 : un usage par section", "Ce qu'il faut pour que ça marche", "Erreurs fréquentes"],
    faq: ["Faut-il des compétences techniques ?"], sources: [] },

  { slug: "estimer-vehicule-occasion-avant-achat", category: "marge", status: "brief",
    keyword: "estimation véhicule occasion professionnel",
    title: "Comment estimer correctement un véhicule d'occasion avant de l'acheter ?",
    excerpt: "Méthode en six étapes : identifier, contrôler, comparer, ajuster, chiffrer les travaux, décider.",
    h2: ["Pourquoi les barèmes ne suffisent pas", "Identifier précisément le véhicule", "Contrôler l'historique", "Comparer le marché réel", "Ajuster selon l'état", "Décider avec une marge cible"],
    faq: ["Quelle différence entre cote et prix de marché ?", "Combien de comparables faut-il ?"], sources: [SRC.histovec] },

  { slug: "calculer-marge-reelle-vehicule-occasion", category: "marge", status: "brief",
    keyword: "marge véhicule occasion",
    title: "Comment calculer la marge réelle d'un véhicule d'occasion ?",
    excerpt: "Prix d'achat, remise en état, frais de portage, coût d'acquisition client, TVA sur marge : la formule complète.",
    h2: ["Marge brute vs marge nette", "Les charges qu'on oublie", "TVA sur marge : ce que ça change", "Exemple chiffré", "Le prix plafond d'achat"],
    faq: ["La TVA sur marge s'applique-t-elle à tous les VO ?", "Comment compter le temps passé ?"], sources: [SRC.cgi297a, SRC.impots] },

  { slug: "prix-achat-vehicule-occasion-marchand", category: "achat", status: "brief",
    keyword: "prix achat véhicule occasion marchand",
    title: "Comment déterminer le bon prix d'achat d'un véhicule pour préserver sa marge ?",
    excerpt: "Partir du prix de revente réaliste et remonter : la seule méthode qui protège la marge.",
    h2: ["Raisonner à l'envers", "Estimer la revente réaliste", "Déduire les charges", "Fixer le plafond", "Négocier avec des arguments chiffrés"],
    faq: ["Faut-il toujours négocier ?"], sources: [] },

  { slug: "histovec-professionnels-automobile", category: "historique", status: "brief",
    keyword: "HistoVec professionnel automobile",
    title: "HistoVec pour les professionnels : quelles informations vérifier avant une reprise ?",
    excerpt: "Ce que le rapport HistoVec contient, ce qu'il ne contient pas, et comment le lire en trois minutes.",
    h2: ["Qu'est-ce qu'HistoVec ?", "Les rubriques à lire en premier", "Gage, opposition, sinistres", "Ce qu'HistoVec ne dit pas", "L'obtenir du vendeur"],
    faq: ["HistoVec est-il gratuit ?", "Un professionnel peut-il l'utiliser ?", "Le rapport est-il opposable ?"], sources: [SRC.histovec, SRC.ants, SRC.servicePublic] },

  { slug: "carvertical-professionnel-automobile", category: "historique", status: "brief",
    keyword: "CarVertical professionnel",
    title: "CarVertical pour un professionnel automobile : quand et pourquoi l'utiliser ?",
    excerpt: "Import, kilométrage à l'étranger, sinistres non déclarés en France : les cas où CarVertical apporte ce qu'HistoVec n'a pas.",
    h2: ["Ce que CarVertical couvre", "Les cas où il est indispensable", "Ce qu'il coûte et ce qu'il évite", "Lire un rapport", "Limites"],
    faq: ["CarVertical remplace-t-il HistoVec ?"], sources: [] },

  { slug: "histovec-vs-carvertical", category: "historique", status: "brief",
    keyword: "HistoVec vs CarVertical",
    title: "HistoVec ou CarVertical : quelles différences pour contrôler un véhicule d'occasion ?",
    excerpt: "Source officielle française contre base de données internationale : ils ne répondent pas aux mêmes questions.",
    h2: ["Deux sources, deux périmètres", "Tableau comparatif", "Quand utiliser l'un, l'autre, les deux", "Coût et délai"],
    faq: ["Lequel choisir pour un véhicule importé ?"], sources: [SRC.histovec] },

  { slug: "cote-la-centrale-strategie-achat-vo", category: "marche", status: "brief",
    keyword: "cote La Centrale professionnel",
    title: "Cote La Centrale : comment l'utiliser dans une stratégie d'achat VO ?",
    excerpt: "Une cote est un point de départ, pas un prix. Comment la confronter au marché réel.",
    h2: ["Ce qu'est une cote", "Cote vs prix de transaction", "L'utiliser comme borne", "Les cas où elle trompe"],
    faq: ["La cote est-elle fiable pour les véhicules rares ?"], sources: [] },

  { slug: "analyser-marche-leboncoin-vehicule-occasion", category: "marche", status: "brief",
    keyword: "Leboncoin voiture professionnel prix marché",
    title: "Comment analyser le marché Leboncoin avant d'acheter un véhicule d'occasion ?",
    excerpt: "Filtres, comparables, délai d'affichage, prix affiché vs prix négocié : lire Leboncoin comme un professionnel.",
    h2: ["Construire la bonne recherche", "Lire les comparables", "Détecter la tension du marché", "Prix affiché et prix réel", "Automatiser la veille"],
    faq: ["Combien d'annonces pour une estimation fiable ?"], sources: [] },

  { slug: "detecter-defauts-voiture-intelligence-artificielle", category: "ia", status: "brief",
    keyword: "analyse photo véhicule IA",
    title: "Peut-on détecter les défauts d'une voiture avec l'intelligence artificielle ?",
    excerpt: "Ce que la vision par ordinateur repère sur une photo, ce qu'elle rate, et comment s'en servir sans s'y fier aveuglément.",
    h2: ["Comment fonctionne l'analyse d'image", "Ce qui se voit sur une photo", "Ce qui ne se voit pas", "Bien photographier un véhicule", "De l'analyse au chiffrage"],
    faq: ["L'IA remplace-t-elle l'inspection physique ?"], sources: [] },

  { slug: "crm-automobile-occasion-leads-relances", category: "crm", status: "brief",
    keyword: "CRM automobile occasion",
    title: "CRM automobile : comment mieux gérer leads, relances et reprises VO",
    excerpt: "Un lead non relancé sous 48 h est perdu. Comment structurer le suivi sans y passer ses soirées.",
    h2: ["Pourquoi les leads VO se perdent", "Le minimum vital d'un CRM VO", "Relances : quand et comment", "Du lead au dossier", "Mesurer ce qui marche"],
    faq: ["Un tableur suffit-il ?"], sources: [] },

  { slug: "formulaire-reprise-automobile-leads-qualifies", category: "crm", status: "brief",
    keyword: "formulaire reprise véhicule",
    title: "Comment créer un formulaire de reprise automobile qui génère des leads qualifiés ?",
    excerpt: "Moins de champs, plus de photos, décodage automatique : ce qui fait qu'un particulier va au bout.",
    h2: ["Ce qu'un particulier accepte de remplir", "L'ordre des champs", "Photos et documents", "Décodage automatique de la plaque", "Ce que le pro reçoit"],
    faq: ["Faut-il demander le VIN ?"], sources: [] },

  { slug: "achat-revente-automobile-analyser-opportunite", category: "achat", status: "brief",
    keyword: "achat revente voiture professionnel",
    title: "Achat-revente automobile : méthode complète pour analyser une opportunité",
    excerpt: "De l'annonce à la décision d'achat, la checklist qu'utilisent les négociants qui ne se trompent pas deux fois.",
    h2: ["Identifier", "Contrôler", "Comparer", "Chiffrer", "Négocier", "Décider", "Préparer la revente"],
    faq: ["Combien de temps pour analyser une opportunité ?"], sources: [SRC.histovec] },

  { slug: "outils-indispensables-marchand-automobile-2026", category: "crm", status: "brief",
    keyword: "marchand automobile logiciel",
    title: "Les outils indispensables d'un marchand automobile en 2026",
    excerpt: "Identification, historique, marché, photo, CRM, facturation : la panoplie minimale, et ce qu'apporte leur unification.",
    h2: ["Identification", "Historique", "Marché", "Vision", "Gestion", "Facturation", "Un outil ou dix ?"],
    faq: [], sources: [] },

  { slug: "vehicules-occasion-meilleures-marges", category: "marge", status: "brief",
    keyword: "rentabilité véhicule occasion",
    title: "Quels véhicules d'occasion offrent les meilleures marges ?",
    excerpt: "Pas de classement absolu — une méthode : rotation, décote, coût de remise en état, demande locale. Et comment la mesurer sur son propre stock.",
    // §97 — jamais de palmarès inventé : méthodologie et données disponibles.
    h2: ["Pourquoi il n'y a pas de réponse universelle", "Les quatre variables de la marge", "Mesurer sur son propre stock", "Segments souvent cités, et pourquoi", "Piloter au lieu de deviner"],
    faq: ["Les électriques d'occasion sont-elles rentables ?"], sources: [SRC.ademe, SRC.insee] },

  { slug: "facturation-electronique-2026-professionnels-automobile", category: "reglementation", status: "brief",
    keyword: "facturation électronique automobile 2026",
    title: "Facturation électronique 2026 : ce que les professionnels automobiles doivent préparer",
    excerpt: "Calendrier officiel, obligations de réception puis d'émission, plateformes, cas de la TVA sur marge : l'essentiel à jour.",
    // §98 — À rédiger UNIQUEMENT à partir des textes en vigueur au moment de la
    // rédaction (impots.gouv.fr, economie.gouv.fr). Aucune date ne doit être
    // avancée de mémoire.
    h2: ["Le calendrier officiel", "Réception puis émission", "Plateformes agréées", "Le cas de la TVA sur marge", "Se préparer sans attendre"],
    faq: ["Un petit garage est-il concerné ?", "Que devient la facture papier ?"], sources: [SRC.impots, SRC.economie] },

  { slug: "logiciel-automobile-classique-ou-assistant-ia", category: "ia", status: "brief",
    keyword: "logiciel garage occasion intelligence artificielle",
    title: "Logiciel automobile classique ou assistant IA : quelle approche pour un professionnel VO ?",
    excerpt: "Modules et boutons contre conversation et outils : deux façons de travailler, et à qui chacune convient.",
    h2: ["Le logiciel à modules", "L'assistant qui orchestre", "Comparatif", "Ce qui change au quotidien", "Comment choisir"],
    faq: ["Peut-on combiner les deux ?"], sources: [] },
];

export const publishedArticles = () => ARTICLES;
export const articleBySlug = (slug) => ARTICLES.find(a => a.slug === slug) || null;
export const categoryLabel = (id) => (CATEGORIES.find(c => c.id === id) || {}).label || id;
